import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { array, hash, get, concat } from '@ember/helper';
import { task, timeout } from 'ember-concurrency';
import { or, and } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsFormTextInputField,
  HdsTable,
  HdsLinkInline,
  HdsPaginationCompact,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Time,
  Flex,
  FlexGrid,
  LimitBanner,
  OperationRefreshRoute,
} from 'core/components';
import { urlFor, resourceTypeFor } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';
import LastOpenedCard from 'hcp/components/page/projects/list/last-opened-card';
import MutationContainer from 'hcp/components/page/projects/mutation-container';

const DEBOUNCE_MS = 500;
/**
 *
 * Displays the list of projects for an organization.
 *
 *
 * ```
 * <Page::Projects::List
 *   @currentPageSize={{this.model.currentPageSize}}
 *   @lastOpenedProject={{this.model.lastOpenedProject}}
 *   @onPageSizeChange={{this.onPageSizeChange}}
 *   @organization={{this.model.organization}}
 *   @pagination={{this.model.pagination}}
 *   @projects={{array project1 project2}}
 * />
 * ```
 *
 * @class PageProjectsList
 *
 */

/**
 * The current page size for paginating the list.
 * @argument currentPageSize
 * @type {Number}
 */

/**
 * The project to render in the card above the project list.
 * @argument lastOpenedProject
 * @type {HashicorpCloudResourcemanagerProject}
 */

/**
 * The function to call when page size changes.
 * @argument onPageSizeChange
 * @type {Function}
 */

/**
 * The current organization.
 * @argument organization
 * @type {Object}
 */

/**
 * The api response's pagination response to render paging controls.
 * @argument pagination
 * @type {HashicorpCloudCommonPaginationResponse}
 */

/**
 * The current organization's projects list.
 * @argument projects
 * @type {Array<HashicorpCloudResourcemanagerProject>}
 */

export default class PageProjectsList extends Component {
  @service router;
  @service analytics;

  get preventDefaultSorting() {
    return () => {
      return 0;
    };
  }

  onSortClick = async (sortBy, sortOrder) => {
    this.analytics.trackEvent(
      `external_ui_hashicorp_project_${sortBy}_sort_${sortOrder}`
    );

    await this.router.transitionTo({
      queryParams: {
        orderby: [`${sortBy} ${sortOrder}`],
        next: undefined,
        prev: undefined,
      },
    });
  };

  handlePageChange = (page) => {
    this.analytics.trackEvent(
      `external_ui_hashicorp_projects_pagination_${page}`
    );
  };

  handleOnPageSizeChanged = async (pageSize) => {
    await this.router.transitionTo({
      queryParams: {
        size: pageSize,
        next: undefined,
        prev: undefined,
      },
    });
  };

  searchProjects = task(
    { restartable: true },
    async ({ target: { value: query } }) => {
      await timeout(DEBOUNCE_MS);
      await this.router.transitionTo({
        queryParams: {
          query,
          next: undefined,
          prev: undefined,
        },
      });
      await timeout(100);
      const input = document.getElementById('projects-list-search');

      if (input) {
        input.focus();
      }
    }
  );

  get paginationQueryFunction() {
    const { nextPageToken, previousPageToken } = this.args?.pagination ?? {};

    return (page) => {
      if (page === 'next') {
        return {
          next: nextPageToken,
          prev: undefined,
        };
      } else {
        return {
          prev: previousPageToken,
          next: undefined,
        };
      }
    };
  }

  <template>
    <MutationContainer as |Container|>
      <OperationRefreshRoute
        @route="cloud.orgs.detail.projects.list"
        @model={{@projects}}
        @resourceType={{resourceTypeFor "project"}}
      />
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Title data-test-projects-list-title>
              {{t "components.page.projects.list.title-short"}}
            </PH.Title>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.IconTile @icon="file-text" />
            <PH.Description data-test-projects-list-description>
              {{t
                "components.page.projects.list.create-or-select-a-project"
                htmlSafe=true
                orgName=@organization.name
                orgDashboardUrl=(urlFor "cloud.orgs.detail.index")
              }}
            </PH.Description>
            <PH.Actions>
              {{#if (and (can "create project" @projects) @projects.length)}}
                <HdsButton
                  @color="primary"
                  @icon="plus"
                  @models={{array @organization.id}}
                  @route="cloud.orgs.detail.projects.create"
                  @text={{t "components.page.projects.create.action"}}
                  data-test-project-create
                />
              {{/if}}
            </PH.Actions>
          </HdsPageHeader>
        </:header>
        <:body>
          <div>
            {{#if @lastOpenedProject}}
              <FlexGrid {{boxMargin "0 0 xl 0"}} as |Grid|>
                <Grid.Item @xs="12" @sm="12" @md="8" @lg="6">
                  <LastOpenedCard
                    @project={{@lastOpenedProject}}
                    data-test-projects-list-last-opened-card
                  />
                </Grid.Item>
              </FlexGrid>
            {{/if}}
            <LimitBanner
              @count={{@projects.length}}
              @variant={{resourceTypeFor "project"}}
            />
            <div {{boxMargin "0 0 xl 0"}}>
              <HdsFormTextInputField
                @id="projects-list-search"
                @isLoading={{this.searchProjects.isRunning}}
                @type="search"
                placeholder={{t "components.page.projects.list.search"}}
                @width="320px"
                @value={{@query}}
                {{on "input" this.searchProjects.perform}}
              />
            </div>
            {{#if @projects.length}}
              <HdsTable
                @model={{@projects}}
                @columns={{array
                  (hash
                    isSortable="true"
                    key="name"
                    label=(t "components.page.projects.list.table.project")
                    sortingFunction=this.preventDefaultSorting
                    width="20%"
                  )
                  (hash
                    key="description"
                    label=(t "components.page.projects.list.table.description")
                  )
                  (hash
                    isSortable="true"
                    key="created_at"
                    label=(t "components.page.projects.list.table.created")
                    sortingFunction=this.preventDefaultSorting
                    width="150px"
                  )
                  (hash
                    align="right"
                    key="options"
                    label=(t "components.page.projects.list.table.actions")
                    width="83px"
                  )
                }}
                @onSort={{this.onSortClick}}
                @valign="middle"
              >
                <:body as |B|>
                  <B.Tr data-table-row>
                    <B.Td>
                      <HdsLinkInline
                        @color="secondary"
                        @route="cloud.orgs.detail.projects.detail"
                        @model={{B.data.id}}
                        class="hds-font-weight-medium"
                        data-test-projects-list-project-id-link
                      >
                        {{B.data.name}}
                      </HdsLinkInline>
                    </B.Td>
                    <B.Td class="hcp-word-wrap-break-all">
                      {{or B.data.description "—"}}
                    </B.Td>
                    <B.Td>
                      <Time
                        @date={{B.data.createdAt}}
                        @display="friendly-only"
                      />
                    </B.Td>
                    <B.Td>
                      {{#let
                        (get @resourcePermissions (concat "project/" B.data.id))
                        as |resourcePermission|
                      }}
                        <Flex @justifyContent="end">
                          <Container.Actions
                            @allowedPermissions={{resourcePermission.allowedPermissions}}
                            @listItem={{true}}
                            @model={{B.data}}
                          />
                        </Flex>
                      {{/let}}
                    </B.Td>
                  </B.Tr>
                </:body>
              </HdsTable>
              <HdsPaginationCompact
                @currentPageSize={{@currentPageSize}}
                @isDisabledPrev={{unless @pagination.previousPageToken true}}
                @isDisabledNext={{unless @pagination.nextPageToken true}}
                @onPageChange={{this.handlePageChange}}
                @onPageSizeChange={{this.handleOnPageSizeChanged}}
                @pageSizes={{array 10 25 50 100}}
                @queryFunction={{this.paginationQueryFunction}}
                @route="cloud.orgs.detail.projects.list"
                @showSizeSelector={{true}}
                {{boxMargin "md 0 0 0"}}
              />
            {{else if @query}}
              <HdsApplicationState
                {{boxPadding "2xl 0"}}
                data-test-projects-list-application-state-filtered
                as |A|
              >
                <A.Header
                  @icon="alert-circle"
                  @title={{t "components.page.projects.list.no-results"}}
                />
                <A.Body
                  @text={{t
                    "components.page.projects.list.try-adjusting-your-applied-filters"
                  }}
                />
                <A.Footer @hasDivider={{true}} as |F|>
                  <F.LinkStandalone
                    @icon="x"
                    @text={{t "components.page.projects.list.clear-filters"}}
                    @route="cloud.orgs.detail.projects.list"
                    @query={{hash query=undefined}}
                    data-test-projects-list-application-state-clear-filters-button
                  />
                </A.Footer>
              </HdsApplicationState>
            {{else}}
              <div
                {{boxPadding "2xl 0"}}
                data-test-projects-list-application-state
              >
                <HdsApplicationState as |A|>
                  <A.Header
                    @title={{t
                      "components.page.projects.list.create-a-project-to-get-started"
                    }}
                  />
                  <A.Body
                    @text={{t
                      "components.page.projects.list.projects-help-you-manage-hcp-resources"
                    }}
                  />
                  {{#if (can "create project" @projects)}}
                    <A.Footer as |F|>
                      <F.Button
                        @color="primary"
                        @icon="plus"
                        @models={{array @organization.id}}
                        @route="cloud.orgs.detail.projects.create"
                        @text={{t "components.page.projects.create.action"}}
                        data-test-projects-list-application-state-link
                      />
                    </A.Footer>
                  {{/if}}
                </HdsApplicationState>
              </div>
            {{/if}}
          </div>
        </:body>
      </CoreLayoutMain>
      <Container.Modals />
    </MutationContainer>
  </template>
}
